import { Attribute } from "../metadata";
import { Constants } from "./Constants";
import { Direction } from "./Direction";
import { FetchXml } from "./FetchXml";
import { FetchXmlBuilder } from "./FetchXmlBuilder";
import { FetchXmlParser } from "./FetchXmlParser";
import { Operators } from "./filter/Operators";
import { Type } from "./filter/Type";

export class FetchXmlFactory {
    public readonly fetch: FetchXmlBuilder.fetch;
    private _parser: FetchXmlParser;
    constructor(initialFetchXml: string) {
        this.fetch = new FetchXml(initialFetchXml).builder;
        this._parser = new FetchXmlParser();
    }
    public setPageSize(pageSize: number) {
        this.fetch.count = pageSize;
        return this;
    }
    public setPageNumber(pageNumber: number, pagingCookie?: string) {
        this.fetch.setPage(pageNumber);
        this.fetch.setPagingCookie(pagingCookie);
        return this;
    }
    public setFilter(expressions: ComponentFramework.PropertyHelper.DataSetApi.FilterExpression[]) {
        for (const expression of expressions) {
            if (!expression || expression.conditions.length === 0) {
                continue;
            }
            this.fetch.entity.addFilter(new FetchXmlBuilder.filter(
                Type.GetNameFromValue(expression.filterOperator),
                expression.conditions.map(x => new FetchXmlBuilder.condition(
                    x.attributeName,
                    Operators.GetNameFromValue(x.conditionOperator),
                    (() => {
                        if (Array.isArray(x.value)) {
                            return x.value.map(y => new FetchXmlBuilder.value(y));
                        }
                        return [new FetchXmlBuilder.value(x.value)];
                    })(),
                    x.entityAliasName
                ))
            ));
        }
        return this;
    }
    public setSorting(sortStatus: ComponentFramework.PropertyHelper.DataSetApi.SortStatus[]) {
        //remove all orders and then add them;
        this.fetch.entity.orders = [];
        const linkedEntities = this._parser.getLinkedEntities(this.fetch);
        linkedEntities.map(x => x.orders = []);

        for (const sort of sortStatus) {
            const isLinkedEntityOrder = sort.name.split('.')[1];
            if (isLinkedEntityOrder) {
                const name = sort.name.split('.')[1];
                const alias = sort.name.split('.')[0];
                const linkedEntity = linkedEntities.find(x => x.alias === alias);
                linkedEntity!.addOrder(new FetchXmlBuilder.order(name, Direction.IsDescending(sort.sortDirection)));
            }
            else {
                this.fetch.entity.addOrder(new FetchXmlBuilder.order(sort.name, Direction.IsDescending(sort.sortDirection)));
            }
        }
        return this;
    }
    public setLinking(linkedEntityExpressions: ComponentFramework.PropertyHelper.DataSetApi.LinkEntityExposedExpression[]) {
        const existingLinkedEntities = new FetchXmlParser().getLinkedEntities(this.fetch);
        linkedEntityExpressions.map(x => {
            if (existingLinkedEntities.find(y => y.alias === x.alias)) {
                //do not add the linked entity if it is already present
                return;
            }
            this.fetch.entity.addLinkEntity(
                new FetchXmlBuilder.linkEntity(
                    x.name,
                    x.from,
                    x.to,
                    x.linkType,
                    x.alias
                ));
        });
        return this;
    }

    public setColumns(columns: ComponentFramework.PropertyHelper.DataSetApi.Column[]) {
        //remove all columns and then add them
        this.fetch.entity.attributes = [];
        const linkedEntities = this._parser.getLinkedEntities(this.fetch);
        linkedEntities.map(x => x.attributes = []);
        columns = columns.filter(x => {
            if (x.name === Constants.RIBBON_BUTTONS_COLUMN_NAME) {
                return false;
            }
            if (x.name.endsWith('__virtual')) {
                return false;
            }
            return true;
        });

        for (const column of columns) {
            const linkedEntityAlias = Attribute.GetLinkedEntityAlias(column.name);
            if (linkedEntityAlias) {
                const attributeName = Attribute.GetNameFromAlias(column.name);
                const linkedEntity = linkedEntities.find(x => x.alias === linkedEntityAlias);
                if (!linkedEntity) {
                    throw new Error(`No linked entity with alias ${linkedEntityAlias} has been found for column ${attributeName}`);
                }
                linkedEntity.addAttribute(new FetchXmlBuilder.attribute(attributeName));
            }
            else {
                this.fetch.entity.addAttribute(new FetchXmlBuilder.attribute(column.name));
            }
        }
        return this;
    }
    public build(): string {
        return this.fetch.toXml();
    }
    public isEqual(initialFetchXml: string): boolean {
        return initialFetchXml === this.fetch.toXml();
    }
}